import {
  Close,
  Content,
  Description,
  Overlay,
  Portal,
  Root,
  Title,
  Trigger,
} from "@radix-ui/react-dialog";
import type {
  ComponentPropsWithoutRef,
  HTMLAttributes,
  ReactNode,
  Ref,
} from "react";

import { cn } from "../lib/utils";
import { Hint } from "./hint";
import { Icon, type IconSymbol } from "./icon";
import { Typography } from "./typography";

const Dialog = Root;
const DialogTrigger = Trigger;
const DialogPortal = Portal;
const DialogClose = Close;

const DialogOverlay = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Overlay> & {
  ref?: Ref<HTMLDivElement>;
}) => (
  <Overlay
    ref={ref}
    className={cn(
      "shadow-custom-dialog fixed inset-[0] z-50 bg-gray-900 opacity-80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
);

DialogOverlay.displayName = Overlay.displayName;

interface DialogContentProps extends ComponentPropsWithoutRef<typeof Content> {
  title: string;
  description?: string;
  className?: string;
  children: ReactNode;
}

const DialogContent = ({
  className,
  children,
  title,
  description,
  ref,
  ...props
}: DialogContentProps & { ref?: Ref<HTMLDivElement> }) => (
  <DialogPortal>
    <DialogOverlay />
    <Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-xl translate-x-[-50%] translate-y-[-50%] bg-background shadow-custom-dialog duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        "bg-white",
        "rounded-t rounded-b",
        "break-words overflow-hidden",
        className,
      )}
      {...props}
    >
      {/* Hidden RadixUI Title for accessibility */}
      <Title className="sr-only">{title}</Title>
      {description && (
        <Description className="sr-only">{description}</Description>
      )}
      {children}
    </Content>
  </DialogPortal>
);

DialogContent.displayName = Content.displayName;

interface DialogFooterProps extends HTMLAttributes<HTMLDivElement> {
  hint?: string;
}

const DialogFooter = ({
  className,
  hint,
  children,
  ...props
}: DialogFooterProps) => (
  <div
    className={cn(
      "flex items-center",
      hint ? "justify-between" : "",
      "border-t-[1px] border-t-gray-200 bg-gray-50",
      "px-6 py-4",
      "rounded-b-md",
      className,
    )}
    {...props}
  >
    {hint && <Hint text={hint} variant="warning" />}
    <div className={cn("flex gap-2", hint ? "" : "justify-between w-full")}>
      {children}
    </div>
  </div>
);

DialogFooter.displayName = "DialogFooter";

interface DialogHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  titleIcon?: IconSymbol;
  description?: string;
}

const DialogHeader = ({
  className,
  title,
  titleIcon,
  description,
  ref,
  children,
  ...props
}: DialogHeaderProps & { ref?: Ref<HTMLDivElement> }) => (
  <div
    ref={ref}
    className={cn(
      "bg-white",
      "px-6 pt-6 pb-5.5 flex items-center gap-4",
      className,
    )}
    {...props}
  >
    {titleIcon && (
      <div
        className={cn(
          "w-7 h-7 rounded-full flex items-center justify-center",
          titleIcon === "info"
            ? "bg-blue-50 text-blue-500"
            : "bg-orange-50 text-orange-500",
        )}
      >
        <Icon icon={titleIcon} aria-hidden="true" />
      </div>
    )}
    {title && (
      <div>
        <Typography variant="h5" darken aria-hidden="true">
          {title}
        </Typography>
        {description && (
          <Typography
            variant="body"
            className="text-gray-900 font-normal"
            aria-hidden="true"
          >
            {description}
          </Typography>
        )}
      </div>
    )}
    {children}
  </div>
);

DialogHeader.displayName = "DialogHeader";

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
};
